
import React from "react";
import { cn } from "@/lib/utils";

interface SkeletonProps {
  className?: string;
  height?: string | number;
  width?: string | number;
  circle?: boolean;
  animate?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className,
  height,
  width,
  circle = false,
  animate = true,
}) => {
  return (
    <div
      className={cn(
        "bg-gray-200 dark:bg-gray-700",
        animate && "animate-pulse",
        circle && "rounded-full",
        !circle && "rounded-md",
        className
      )}
      style={{
        height: height,
        width: width,
      }}
    />
  );
};

interface SkeletonTableRowsProps {
  rows?: number;
  columns?: number;
  className?: string;
}

export const SkeletonTableRows: React.FC<SkeletonTableRowsProps> = ({
  rows = 5,
  columns = 4,
  className,
}) => {
  // Instead of returning a wrapping div, we return an array of TableRow elements
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={rowIndex} className={cn("animate-pulse", className)}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td key={colIndex} className="p-4">
              <div 
                className={cn(
                  "h-4 bg-gray-200 dark:bg-gray-700 rounded-md", 
                  colIndex === 0 ? "w-[250px]" : "w-full max-w-[200px]"
                )}
              ></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

interface SkeletonCardProps {
  className?: string;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({ className }) => {
  return (
    <div className={cn("space-y-3 rounded-md border p-4", className)}>
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
    </div>
  );
};

export const SkeletonCards: React.FC<{ count?: number }> = ({ count = 3 }) => {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
};
